class PredictiveSearchComponent extends HTMLElement {

  /** @type {number | null} */
  #animationTimeoutId = null;
  #isUserTyping = false;
  #animationCount = 0;
  #animationIndex = 0;
  #isDeleting = false;

  constructor() {
    super();

    this.#updateSubmitButtonReference();

    /** @type {HTMLInputElement} */
    this.searchInput = /** @type {HTMLInputElement} */ (this.querySelector('.search-input'));
    /** @type {HTMLElement} */
    this.resetButton = /** @type {HTMLElement} */ (this.querySelector('.predictive-search__reset-button'));

    if (!this.searchInput || !this.resetButton) return;

    this.searchInput.addEventListener('input', () => {
      this.#updateSubmitButtonVisibility();
      this.#handleUserInput();
    });

    this.searchPlaceholder = (this.getAttribute('data-search-placeholder') || '')
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean);

    this.resetButton?.addEventListener('click', () => {
      this.resetInput();
    });

    this.searchInput.addEventListener('focus', () => {
      if (this.searchInput.value.length > 0) {
        this.#isUserTyping = true;
        this.#stopAnimation();
      }
    });

    this.searchInput.addEventListener('blur', () => {
      if (this.searchInput.value.length === 0) {
        this.#isUserTyping = false;
        setTimeout(() => {
          if (!this.#isUserTyping && this.searchInput.value.length === 0) {
            this.#startAnimation();
          }
        }, 100);
      }
    });

    this.#startAnimation();
  }

  #updateSubmitButtonReference() {
    this.submitBtnWrapper = this.querySelector('.predictive-search-form__submit-button-wrapper');
  }

  #updateSubmitButtonVisibility() {
    if (this.searchInput.value.length > 0) {
      this.submitBtnWrapper?.classList.add('show');
      this.resetButton.classList.add('active');
    } else {
      this.submitBtnWrapper?.classList.remove('show');
      this.resetButton.classList.remove('active');
    }
  }

  #handleUserInput() {
    if (this.searchInput.value.length > 0) {
      this.#isUserTyping = true;
      this.#stopAnimation();
    } else {
      this.#isUserTyping = false;
      setTimeout(() => {
        if (!this.#isUserTyping && this.searchInput.value.length === 0) {
          this.#startAnimation();
        }
      }, 50);
    }
  }

  #stopAnimation() {
    if (this.#animationTimeoutId) {
      clearTimeout(this.#animationTimeoutId);
      this.#animationTimeoutId = null;
    }
  }

  #startAnimation() {
    const texts = this.searchPlaceholder;
    if (!texts || texts.length === 0) return;

    this.#stopAnimation();
    this.#animationIndex = 0;
    this.#isDeleting = false;
    this.#runAnimation();
  }

  #runAnimation() {
    const texts = this.searchPlaceholder;
    if (!texts || texts.length === 0) return;

    this.#stopAnimation();

    if (this.#isUserTyping) return;

    const speed = 100;
    const currentText = texts[this.#animationCount];
    if (!currentText) return;

    if (this.#isDeleting) {
      this.searchInput.placeholder = currentText.slice(0, --this.#animationIndex);
    } else {
      this.searchInput.placeholder = currentText.slice(0, ++this.#animationIndex);
    }

    if (!this.#isDeleting && this.#animationIndex === currentText.length) {
      this.#isDeleting = true;
      this.#animationTimeoutId = setTimeout(() => this.#runAnimation(), 1000);
      return;
    } else if (this.#isDeleting && this.#animationIndex === 0) {
      this.#isDeleting = false;
      this.#animationCount = (this.#animationCount + 1) % texts.length;
    }

    this.#animationTimeoutId = setTimeout(() => this.#runAnimation(), this.#isDeleting ? speed / 2 : speed);
  }

  resetInput() {
    this.searchInput.value = '';
    this.resetButton.classList.remove('active');
    this.#isUserTyping = false;
    this.#startAnimation();
    this.#updateSubmitButtonReference();
    this.#updateSubmitButtonVisibility();
  }
}

customElements.define('predictive-search-component', PredictiveSearchComponent);
